import { Alert, Col, Flex, Form, Input, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { AddIcon } from '@/components/Icons';
import { SelectiveTag } from '@/components/SelectiveTag';
import { useEffect, useState } from 'react';
import { useCreateRole, useUpdateRole, useFetchPermissions } from '@/hooksApi';
import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { Store } from 'antd/es/form/interface';
import { Role, PermissionName, RuleName, HIDDEN_PERMISSION_NAMES } from '@/types';

// Permission dependency map: permission -> prerequisites
const PERMISSION_DEPENDENCIES: Record<string, string[]> = {
  [PermissionName.ORDER_AND_SHIPMENT]: [PermissionName.DASHBOARD],
  [PermissionName.WITHDRAW_REMITTANCE]: [PermissionName.FINANCE],
  [PermissionName.CREATE_ORDER]: [PermissionName.DASHBOARD, PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.UPDATE_ORDER]: [PermissionName.DASHBOARD, PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.BULK_IMPORT_ORDER]: [PermissionName.DASHBOARD, PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.DELETE_ORDER]: [PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.CANCEL_ORDER]: [PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.RESOLVE_OR_REQUEST_RTO]: [PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.REQUEST_PICK_UP]: [PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.CREATE_TICKET]: [PermissionName.DASHBOARD],
  [PermissionName.ADD_COMMENT]: [PermissionName.DASHBOARD],
  [PermissionName.SOLVE_TICKET]: [PermissionName.DASHBOARD],
};

interface Props {
  roleUID?: string;
  roleData?: Role;
  onClose: () => void;
}
const RoleForm = ({ roleUID, roleData, onClose }: Props) => {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [dependencyWarning, setDependencyWarning] = useState<string>('');
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const { mutate: createRole } = useCreateRole();
  const { mutate: updateRole } = useUpdateRole();

  const { data: permissionsList } = useFetchPermissions();

  const onFinish = async (values: Store) => {
    if (roleUID === '') {
      createRole(
        {
          name: values.name,
          description: values.description,
          permission_uids: selectedTags,
        },
        {
          onSuccess: () => {
            onClose();
            queryClient.invalidateQueries(['teamMember-roles']);
          },
        },
      );
    } else {
      updateRole(
        {
          uid: roleUID,
          name: values.name,
          description: values.description,
          permission_uids: selectedTags,
        },
        {
          onSuccess: () => {
            onClose();
            queryClient.invalidateQueries(['teamMember-roles']);
            queryClient.invalidateQueries(['teamMember-users']);
          },
        },
      );
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error('Form submission failed:', errorInfo);
    message.error(t('submission_failed'));
  };

  useEffect(() => {
    if (!roleData) {
      clearForm();
    } else {
      form.setFieldsValue(roleData);
      const initialPermissionUids = roleData.permissions?.map((permission) => permission.uid) ?? [];
      setSelectedTags(initialPermissionUids);
      form.setFieldsValue({ permission_uids: initialPermissionUids });
    }
    return () => {
      clearForm();
    };
  }, [roleData]);

  const clearForm = () => {
    form.resetFields();
    setSelectedTags([]);
    form.setFieldsValue({ permission_uids: [] });
  };

  /**
   * Get all prerequisites for a given permission (including nested prerequisites)
   */
  const getPrerequisites = (permissionName: string): string[] => {
    const prerequisites = PERMISSION_DEPENDENCIES[permissionName] || [];
    const allPrerequisites = new Set<string>(prerequisites);

    // Recursively get prerequisites of prerequisites
    prerequisites.forEach((prereq) => {
      const nestedPrereqs = getPrerequisites(prereq);
      nestedPrereqs.forEach((p) => allPrerequisites.add(p));
    });

    return Array.from(allPrerequisites);
  };

  /**
   * Get all permissions that depend on a given permission
   */
  const getDependents = (permissionName: string): string[] => {
    const dependents: string[] = [];

    Object.entries(PERMISSION_DEPENDENCIES).forEach(([permission, prerequisites]) => {
      if (prerequisites.includes(permissionName)) {
        dependents.push(permission);
        // Also get dependents of this dependent (recursive)
        const nestedDependents = getDependents(permission);
        dependents.push(...nestedDependents);
      }
    });

    return Array.from(new Set(dependents));
  };

  /**
   * Handle permission selection with dependency warning
   */
  const handlePermissionChange = (permissionUid: string, checked: boolean) => {
    // Find the permission name from the permissions list
    const permission = permissionsList?.object?.find((p) => p.uid === permissionUid);
    const permissionName = permission?.name || '';

    let updatedList = [...selectedTags];

    if (checked) {
      // Add the permission
      updatedList.push(permissionUid);
    } else {
      // Remove the deselected permission
      updatedList = updatedList.filter((uid) => uid !== permissionUid);

      // Note: We do NOT automatically unselect dependent permissions
      // The user should manually unselect them if needed
    }

    // Remove duplicates
    updatedList = Array.from(new Set(updatedList));

    // Check all selected permissions for missing prerequisites
    const allMissingDependencies: { permission: string; prerequisites: string[] }[] = [];

    updatedList.forEach((uid) => {
      const perm = permissionsList?.object?.find((p) => p.uid === uid);
      if (!perm) return;

      const prerequisites = getPrerequisites(perm.name);
      const missingPrerequisites: string[] = [];

      prerequisites.forEach((prereqName) => {
        const prereqPermission = permissionsList?.object?.find((p) => p.name === prereqName);
        if (prereqPermission && !updatedList.includes(prereqPermission.uid)) {
          missingPrerequisites.push(t(`permission.${prereqName}`));
        }
      });

      if (missingPrerequisites.length > 0) {
        allMissingDependencies.push({
          permission: t(`permission.${perm.name}`),
          prerequisites: missingPrerequisites,
        });
      }
    });

    // Update warning based on all missing dependencies
    if (allMissingDependencies.length > 0) {
      // Collect all permissions with issues
      const permissionsWithIssues = allMissingDependencies.map((dep) => dep.permission);

      // Collect all unique missing prerequisites
      const allMissingPrereqs = new Set<string>();
      allMissingDependencies.forEach((dep) => {
        dep.prerequisites.forEach((prereq) => allMissingPrereqs.add(prereq));
      });

      // Format the warning message with i18n pluralization
      // Use translated 'and' separator for proper localization
      const andSeparator = t('and');
      const permissionsList = permissionsWithIssues.join(`" ${andSeparator} "`);
      const prerequisitesList = Array.from(allMissingPrereqs).join(` ${andSeparator} `);

      // Explicitly select the correct translation key based on count
      const translationKey =
        allMissingPrereqs.size === 1
          ? 'requires_the_following_permissions_one'
          : 'requires_the_following_permissions_other';

      setDependencyWarning(
        t(translationKey, {
          permissions: permissionsList,
          prerequisites: prerequisitesList,
        }),
      );
    } else {
      // Only clear warning when all dependencies are satisfied
      setDependencyWarning('');
    }

    setSelectedTags(updatedList);
    form.setFieldsValue({ permission_uids: updatedList });
  };

  return (
    <Form
      form={form}
      name='TeamMember-form'
      className='py-2'
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={roleData}
      clearOnDestroy
    >
      <Row gutter={[16, 0]} className='mt-4'>
        <Col lg={24} md={24} xs={24}>
          <Form.Item
            name='name'
            rules={[
              { required: true, message: t('this_field_is_required') },
              {
                pattern: /^[a-z0-9_]*$/,
                message: t('only_english_letters_numbers_and_underscore_are_allowed'),
              },
              {
                pattern: /^[a-z0-9_]+$/,
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/[A-Z]/.test(value)) {
                    return Promise.reject(t('letters_must_be_lowercase'));
                  }
                  return Promise.resolve();
                },
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const presetRoles = Object.values(RuleName);
                  if (presetRoles.includes(value.toLowerCase())) {
                    return Promise.reject(t('role_name_cannot_be_preset'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              suffix={<span className='text-customRed text-base'>*</span>}
              variant='outlined'
              placeholder={t('english_name')}
              className='custom-input border custom-select-placeholder border-solid border-customGrayShade5 placeholder:text-GrayShade4 placeholder:font-[400] placeholder:text-[16px] placeholder:leading-[20.48px] h-[56px] rounded-lg w-full'
            />
          </Form.Item>
        </Col>
        <Col lg={24} md={24} xs={24}>
          <Form.Item
            name='description'
            rules={[
              { required: true, message: t('this_field_is_required') },
              {
                pattern:
                  /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF0-9\s._-]+$/,
                message: t('only_arabic_letters_numbers_and_symbols_are_allowed'),
              },
            ]}
          >
            <Input
              suffix={<span className='text-customRed text-base'>*</span>}
              variant='outlined'
              placeholder={t('arabic_name')}
              className='custom-input border custom-select-placeholder border-solid border-customGrayShade5 placeholder:text-GrayShade4 placeholder:font-[400] placeholder:text-[16px] placeholder:leading-[20.48px] h-[56px] rounded-lg w-full'
            />
          </Form.Item>
        </Col>
        <Col lg={24} md={24} xs={24}>
          {dependencyWarning && (
            <Alert
              message={dependencyWarning}
              type='warning'
              showIcon
              closable
              onClose={() => setDependencyWarning('')}
              className='mb-4'
            />
          )}
          <Form.Item
            name='permission_uids'
            rules={[{ required: true, message: t('please_select_at_least_one_permission') }]}
          >
            <Flex gap={4} wrap>
              {/* Carrier permissions are never offered (HIDDEN_PERMISSION_NAMES). */}
              {permissionsList?.object
                ?.filter((permission) => !HIDDEN_PERMISSION_NAMES.includes(permission.name))
                .map((permission) => (
                  <SelectiveTag
                    key={permission.uid}
                    selectedTags={selectedTags as string[]}
                    tagKey={permission.uid}
                    tagLabel={permission.name ? t(`permission.${permission.name}`) : '---'}
                    handleChange={handlePermissionChange}
                  />
                ))}
            </Flex>
          </Form.Item>
        </Col>
      </Row>

      <Flex className='mt-8' justify='end' gap={16}>
        <button
          type='button'
          onClick={onClose}
          className='flex items-center gap-1.5 h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(245,245,243)] transition-colors uppercase'
        >
          {t('cancel')}
        </button>
        <button
          type='submit'
          disabled={selectedTags.length === 0 || dependencyWarning !== ''}
          className='flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors uppercase'
        >
          {roleUID ? undefined : <AddIcon width={20} height={20} />}
          {roleUID ? t('update') : t('create')}
        </button>
      </Flex>
    </Form>
  );
};

export default RoleForm;
